import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRealError } from '@/lib/invokeWithRealError';
import { toast } from '@/hooks/use-toast';
import { keepOnlyDigits } from '@/utils/formatters';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { feedbackSolicitacaoFatura, type SolicitacaoFaturaResultado } from '@/lib/solicitacaoFaturaFeedback';

export function useSolicitarFatura(clienteId: string | undefined) {
  const queryClient = useQueryClient();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'A',
    baseInterval: POLLING_INTERVALS.PESO_A,
  });

  const { data: solicitacoes, isLoading } = useQuery({
    queryKey: ['solicitacoes-fatura', clienteId],
    queryFn: async () => {
      if (!clienteId) return [];

      try {
        const { data, error } = await supabase
          .from('v_resumo_solicitacoes_fatura')
          .select('*')
          .eq('cliente_id', clienteId)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        onQuerySuccess();
        return data;
      } catch (e) {
        onQueryError(e);
        throw e;
      }
    },
    enabled: !!clienteId,
    staleTime: 0,
    refetchInterval,
    refetchOnWindowFocus: false,
  });

  const solicitar = useMutation({
    mutationFn: async ({ cnpj, linha_plano, linha_solicitante }: { cnpj?: string; linha_plano: string; linha_solicitante: string }) => {
      // Helper extrai motivo REAL quando edge function retorna non-2xx
      // (em vez do genérico "Edge Function returned a non-2xx status code")
      const { data, error, mensagem } = await invokeWithRealError('solicitar-fatura', {
        body: {
          cliente_id: clienteId || undefined,
          cnpj: cnpj || undefined,
          linha_plano: keepOnlyDigits(linha_plano),
          linha_solicitante: keepOnlyDigits(linha_solicitante),
          origem: 'MANUAL',
        },
      });

      if (error) throw new Error(mensagem || 'Erro desconhecido');
      if (!data?.ok) throw new Error(data?.mensagem || data?.error || 'Falha ao solicitar fatura');
      return data as SolicitacaoFaturaResultado;
    },
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: ['solicitacoes-fatura', clienteId] });
      // Invalidar cache da página centralizada também (sincronização bidirecional)
      queryClient.invalidateQueries({ queryKey: ['todas-faturas'] });
      toast(feedbackSolicitacaoFatura(resultado));
    },
    onError: (error: unknown) => {
      toast({
        title: '❌ Erro',
        description: error instanceof Error ? error.message : 'Não foi possível solicitar a fatura.',
        variant: 'destructive',
      });
    },
  });

  return {
    solicitacoes,
    isLoading,
    solicitar,
    temAndamento: (solicitacoes || []).some(s => {
      const statusEmAndamento = ['AGUARDANDO_ENVIO', 'ENVIADO_N8N', 'PROCESSANDO', 'PROCESSANDO_BEE360'];
      
      if (!statusEmAndamento.includes(s.status)) return false;
      
      // Verificar timeout com fallback seguro (2 horas = 120 minutos)
      if (s.created_at) {
        try {
          const agora = Date.now();
          const criado = new Date(s.created_at).getTime();
          
          // Se data inválida (NaN), libera
          if (isNaN(criado)) return false;
          
          const diferencaMinutos = (agora - criado) / (1000 * 60);
          
          // Libera se passou mais de 2 horas
          if (diferencaMinutos >= 120) return false;
        } catch {
          // Erro ao parsear data - libera (fallback seguro)
          return false;
        }
      } else {
        // Sem created_at - libera (fallback seguro)
        return false;
      }
      
      return true; // Ainda em andamento e dentro do timeout
    }),
  };
}

// Função removida - use formatarTelefone from @/utils/formatters
